// Direct REST API client for contracts
// Use relative URL for production (nginx proxy) or env var for development
const CONTRACTS_API_BASE =
  process.env.REACT_APP_CONTRACTS_API_BASE || "http://localhost:5010";

export async function fetchContracts(page = 0, pageSize = 25) {
  try {
    const url = `${CONTRACTS_API_BASE}/contracts?page=${page}&page_size=${pageSize}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log("Fetched contracts:", data);
    return data;
  } catch (error) {
    console.error("Error fetching contracts:", error);
    throw error;
  }
}

export async function fetchContract(address) {
  try {
    const url = `${CONTRACTS_API_BASE}/contracts/${address}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching contract:", error);
    throw error;
  }
}

export async function fetchContractEvents(address, page = 0, pageSize = 25) {
  try {
    const url = `${CONTRACTS_API_BASE}/contracts/${address}/events?page=${page}&page_size=${pageSize}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching contract events:", error);
    return [];
  }
}

export async function fetchContractCalls(address, page = 0, pageSize = 25) {
  try {
    const url = `${CONTRACTS_API_BASE}/contracts/${address}/calls?page=${page}&page_size=${pageSize}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching contract calls:", error);
    return [];
  }
}

export async function fetchContractABI(address) {
  try {
    const url = `${CONTRACTS_API_BASE}/contracts/${address}/abi`;
    const response = await fetch(url);

    if (!response.ok) {
      if (response.status === 404) {
        return null; // No ABI found
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.abi; // Return just the ABI data
  } catch (error) {
    console.error("Error fetching contract ABI:", error);
    return null;
  }
}

export async function saveContractABI(address, abi) {
  try {
    const url = `${CONTRACTS_API_BASE}/contracts/${address}/abi`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ abi }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving contract ABI:", error);
    throw error;
  }
}

export async function deleteContractABI(address) {
  try {
    const url = `${CONTRACTS_API_BASE}/contracts/${address}/abi`;
    const response = await fetch(url, {
      method: "DELETE",
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting contract ABI:", error);
    throw error;
  }
}

// Helper function to decode contract method from input data
function decodeContractMethod(inputData, contractABI) {
  if (!inputData || !contractABI) return null;

  try {
    // Remove 0x prefix if present
    const cleanInput = inputData.startsWith("0x")
      ? inputData.slice(2)
      : inputData;

    // First 8 characters (4 bytes) are the method selector
    const selector = cleanInput.slice(0, 8);

    // For ink! contracts, try to match selector to ABI methods
    if (Array.isArray(contractABI)) {
      for (const method of contractABI) {
        const methodName = method.name || method.label;
        if (method.selector) {
          // Remove 0x prefix from selector for comparison
          const methodSelector = method.selector.startsWith("0x")
            ? method.selector.slice(2)
            : method.selector;
          if (methodSelector === selector) {
            return methodName;
          }
        }
      }
    } else if (contractABI.spec && contractABI.spec.messages) {
      for (const method of contractABI.spec.messages) {
        const methodName = method.name || method.label;
        if (method.selector) {
          const methodSelector = method.selector.startsWith("0x")
            ? method.selector.slice(2)
            : method.selector;
          if (methodSelector === selector) {
            return methodName;
          }
        }
      }
    }

    // If no match found, return the selector
    return `0x${selector}`;
  } catch (error) {
    console.error("Error decoding contract method:", error);
    return null;
  }
}

export async function fetchContractTransactions(
  address,
  page = 0,
  pageSize = 25,
) {
  try {
    // Use the dedicated contract extrinsics endpoint
    const url = `${CONTRACTS_API_BASE}/contracts/${address}/extrinsics?page=${page}&pageSize=${pageSize}`;
    const response = await fetch(url);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      );
    }

    const data = await response.json();
    console.log("Fetched contract transactions:", data);

    return data;
  } catch (error) {
    console.error("Error fetching contract transactions:", error);
    throw error;
  }
}

export async function callContractMethod(
  address,
  method,
  args = [],
  abi,
  value = 0,
  gasLimit = null,
  caller = null,
) {
  try {
    const url = `${CONTRACTS_API_BASE}/contracts/${address}/call`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        method,
        args,
        abi,
        value,
        gasLimit,
        caller,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error calling contract method:", error);
    throw error;
  }
}

export async function deployContract(deploymentData) {
  try {
    const url = `${CONTRACTS_API_BASE}/contracts/deploy`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(deploymentData),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error deploying contract:", error);
    throw error;
  }
}

export async function uploadContractCode(wasmFile, signer = "//Alice") {
  try {
    const url = `${CONTRACTS_API_BASE}/contracts/upload`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ wasmFile, signer }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error uploading contract code:", error);
    throw error;
  }
}
